<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {mapActions, useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = withDefaults(defineProps<{
  groupIdx: number;
  presetButton?: boolean;
  prioSettings?: string[];
  stoneLayout?: string;
}>(), {
  presetButton: true,
  stoneLayout: "single"
});
const emit = defineEmits(["patch", "show-presets"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3DiscsRatio.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, but - like itemV3StonesStoneSize.vue /
 * itemV3StonesPosition.vue - the variant is fetched per stone group via
 * getItemVariants(itemIdx, "stones.<groupIdx>.stone_setting") instead of a fixed idx.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone setting - stones.<groupIdx>.stone_setting
 */

const stoneSetting = ref({
  "stone_setting": {}
} as any)

const getStoneSettingData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneSetting.value.stone_setting = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_setting")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneSettingData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneSettingData(props.groupIdx)
}, {deep: true})

/**
 * setting variants (transferred from itemV3DiscsRatio.vue's "color variants")
 */

const active = computed(() => {
  return stoneSetting.value.stone_setting[0]
})

const variantsList = computed(() => active.value?.values || [])

const matchedIdx = computed(() => variantsList.value.findIndex((v: any) => v.match))

/*
 * focus values (transferred from itemV3DiscsRatio.vue's "focus values")
 */

const focusValueIdx = props.prioSettings || [
  // idx must match one of the stone_setting option keys (see locales/*.json)
  "CHANNEL",
  "PAVE",
  "BEZEL"
]

const getFocusValueIdx = computed(() => {
  if (matchedIdx.value !== -1 && !focusValueIdx.includes(variantsList.value[matchedIdx.value].idx)) {
    return [
      variantsList.value[matchedIdx.value].idx,
      ...focusValueIdx.slice(0, -1)
    ]
  }

  return focusValueIdx
})

// only the values that are part of focusValueIdx should ever be shown; if that list is
// larger than 5, the first 5 are shown directly and the rest are hidden behind "more"
const focusVariantsList = computed(() => getFocusValueIdx.value
    .map((idx: string) => variantsList.value.find((v: any) => v.idx === idx))
    .filter((v: any) => !!v))

const visibleFocusValues = computed(() => focusVariantsList.value.slice(0, 20))

const moreFocusValues = computed(() => focusVariantsList.value.slice(20))

const hasMoreValues = computed(() => moreFocusValues.value.length > 0)

// whether the "more" values (list entry with the right-arrow) have been revealed inline
const showMore = ref(false)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * Stone addon
 */

const hasStoneAddon = computed(() => {
  // if in session.value.items[itemIdx.value].variants an object exist with idx = "stones.<props.groupIdx>.stone_addon" and key != ""
  const addon = getStoneAddon.value
  return Boolean(addon && addon.key && addon.key !== "")
})

const getStoneAddon = computed(() => {
  if (session.value?.items?.[itemIdx.value]?.variants) {
    const variants = session.value.items[itemIdx.value].variants
    const list = Array.isArray(variants) ? variants : Object.values(variants)
    return list.find((v: any) => v?.idx === `stones.${props.groupIdx}.stone_addon`) || {}
  }
  return {}
})

/**
 * stone setting
 */

const stoneSettingUrlAddon = computed(() => {
  return (props.stoneLayout !== "single") ? "-" + props.stoneLayout : ""
})

const newIcons = [
  "c391",
  "c281",
  "c66",
  "c31",
  "c22",
  "c21",
  "c18",
  "c17",
  "c5"
]

// true if the currently selected stone addon is one of the "new icon" addons
const useNewIcons = computed(() => {
  const key = getStoneAddon.value?.key
  return !!key && newIcons.includes(String(key).toLowerCase())
})


</script>

<template>
  <div v-if="active" class="w-full">
    <ul class="flex md:gap-x-1 flex-wrap mb-2 w-full justify-center ">
      <!-- stone setting list - focus values (only values that are part of focusValueIdx) -->
      <li v-for="(variantData, variantIdx) in visibleFocusValues" :key="variantIdx"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
          @click="handleValuePatch(active.idx, variantData)"
          :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
      >
        <div
            class="h-12 w-13 md:h-12 md:w-15 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center">
          <img
              :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_setting/400x300/${variantData.idx.toLowerCase()}${stoneSettingUrlAddon}.webp`"
              :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_setting/800x600/${variantData.idx.toLowerCase()}${stoneSettingUrlAddon}.webp`"
              class="w-full h-full object-cover relative"
          >
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 md:max-w-15 mx-auto text-center mt-1">
          {{ variantData.title }}
        </span>
      </li>
      <!-- stone addon -->
      <li
          v-if="hasStoneAddon"
          class="border-l border-l-[#e7ddcc] pl-2 relative flex flex-col items-center py-2 px-1 hover:bg-[#f3f0e9]"
      >
        <!-- remove button -->
        <button
            class="absolute top-0 right-0 p-1 rounded-full hover:bg-white hover:text-brand-main z-10 bg-brand-main text-white cursor-pointer"
            @click="handleValuePatch(getStoneAddon.idx, {idx: ''})"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
               stroke="currentColor" class="w-4 h-4">
            <path stroke-linecap="round" stroke-linejoin="round"
                  d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
        </button>
        <div class="h-12 w-13 md:h-12 md:w-15 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center">
          <img
              :src="useNewIcons
                ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/400x300/${getStoneAddon.key.toLowerCase()}.webp`
                : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/300x300/addon_${getStoneAddon.key.toLowerCase()}.webp`"
              :srcset="useNewIcons
                ? `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/800x600/${getStoneAddon.key.toLowerCase()}.webp`
                : `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_addon/600x600/addon_${getStoneAddon.key.toLowerCase()}.webp`"
              class="w-full h-full object-cover relative"
          >
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          {{ getStoneAddon.text}}
        </span>
      </li>
      <!-- "more" trigger - placed as the last entry of the focus list, looks like another list item -->
      <li v-if="hasMoreValues && !showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = true"
      >
        <div class="h-12 w-12 md:h-12 md:w-15 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 -rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-12 mx-auto text-center mt-1">
          Mehr
        </span>
      </li>
      <!-- stone setting list - more values (revealed inline once the "more" entry above was clicked) -->
      <template v-if="showMore">
        <li v-for="(variantData, variantIdx) in moreFocusValues" :key="'more-' + variantIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(active.idx, variantData)"
            :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': variantData.match,
              'border-2 border-transparent': !variantData.match
            }"
        >
          <div
              class="h-12 w-13 md:h-12 md:w-15 rounded-2xl bg-white overflow-hidden relative flex items-center justify-center">
            <img
                :src="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_setting/400x300/${variantData.idx.toLowerCase()}${stoneSettingUrlAddon}.webp`"
                :srcset="`https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_setting/800x600/${variantData.idx.toLowerCase()}${stoneSettingUrlAddon}.webp`"
                class="w-full h-full object-cover relative"
            >
          </div>
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 md:max-w-15 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
        </li>
      </template>
      <!-- "less" trigger - placed as the last entry once "more" is revealed, collapses the list again -->
      <li v-if="showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = false"
      >
        <div class="h-12 w-13 md:h-12 md:w-15  rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 md:max-w-15 mx-auto text-center mt-1">
          Weniger
        </span>
      </li>
      <!-- "other" trigger - placed as the very last entry of the list, triggers the "show presets"
           variable owned by the parent component, which uses it to show item-v3-stones-presets -->
      <li
          v-if="presetButton"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="emit('show-presets')"
      >
        <div class="h-12 w-13 md:h-12 md:w-15 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" class="size-4 shrink-0 text-[#555956]">
            <circle cx="3" cy="8" r="1.5" fill="currentColor"/>
            <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
            <circle cx="13" cy="8" r="1.5" fill="currentColor"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 md:max-w-15 mx-auto text-center mt-1">
          Andere
        </span>
      </li>
    </ul>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
